import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  PageHeader,
  Row,
  Col,
  Card,
  Statistic,
  Button,
  List,
  Tag,
  Avatar,
  Empty,
  Typography,
  Result,
  Spin,
} from "antd";
import {
  DashboardOutlined,
  ProjectOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  WarningOutlined,
  PlusOutlined,
  ArrowRightOutlined,
} from "@ant-design/icons";
import Auth from "auth";
import { handleApiError, MyBreadcrumb, accessControls } from "utils";
import { ticketDashboardService } from "services/tickets";
import { PRIORITY_META, userDisplayName, userInitials, isOverdue } from "../constants";
import "../_styles.css";

const { Text } = Typography;

export default function TicketDashboard() {
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [permissions, setPermissions] = useState(null);

  const isSuperAdmin = Number(Auth.getRole()) === 10;

  useEffect(() => {
    accessControls
      .fetch()
      .then(setPermissions)
      .catch(() => setPermissions({}));
  }, []);

  useEffect(() => {
    setLoading(true);
    ticketDashboardService()
      .then((res) => {
        setData(res.data.item || {});
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        handleApiError(err);
      });
  }, []);

  const canAccess =
    isSuperAdmin ||
    (permissions && accessControls.can(permissions, "TicketSystem.read:any"));

  if (permissions !== null && !canAccess) {
    return (
      <Result
        status="403"
        title="Access Denied"
        subTitle="The Ticket System is available to Super Admin only."
      />
    );
  }

  const stats = (data && data.stats) || {};
  const myTickets = (data && data.my_tickets) || [];
  const projects = (data && data.projects) || [];
  const recentActivity = (data && data.recent_activity) || [];

  if (permissions === null && !isSuperAdmin) {
    return (
      <div style={{ textAlign: "center", padding: 80 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="ticket-system">
      <MyBreadcrumb
        items={[
          { label: "Home", path: "/" },
          { label: "Tickets", path: "" },
          { label: "Dashboard", path: "" },
        ]}
      />
      <PageHeader
        ghost={false}
        title="Ticket Dashboard"
        subTitle="Track projects, tasks, and team progress at a glance"
        avatar={{ icon: <DashboardOutlined /> }}
        extra={[
          <Button key="projects" onClick={() => history.push("/tickets/projects")}>
            All Projects
          </Button>,
          <Button
            key="new"
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => history.push("/tickets/projects/create")}
          >
            New Project
          </Button>,
        ]}
      />

      <Row gutter={[16, 16]} className="ticket-dashboard-stats">
        <Col xs={24} sm={12} lg={6}>
          <Card className="ticket-stat-card" loading={loading}>
            <Statistic title="Active Projects" value={stats.projects || 0} prefix={<ProjectOutlined />} />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card className="ticket-stat-card" loading={loading}>
            <Statistic title="Open Tickets" value={stats.open_tickets || 0} prefix={<ClockCircleOutlined />} />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card className="ticket-stat-card" loading={loading}>
            <Statistic
              title="Completed"
              value={stats.done_tickets || 0}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: "#22c55e" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card className="ticket-stat-card" loading={loading}>
            <Statistic
              title="Overdue"
              value={stats.overdue_tickets || 0}
              prefix={<WarningOutlined />}
              valueStyle={{ color: stats.overdue_tickets ? "#dc2626" : undefined }}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={14}>
          <Card title="My Open Tickets" loading={loading}>
            {myTickets.length === 0 ? (
              <Empty description="No tickets assigned to you" />
            ) : (
              <List
                dataSource={myTickets}
                renderItem={(ticket) => {
                  const pm = PRIORITY_META[ticket.priority] || PRIORITY_META.MEDIUM;
                  const overdue = isOverdue(ticket.due_date, ticket.status);
                  const projectId =
                    ticket.project && (ticket.project._id || ticket.project);
                  return (
                    <List.Item
                      style={{ cursor: "pointer" }}
                      onClick={() => {
                        if (projectId) {
                          history.push("/tickets/view/" + ticket._id + "?tab=comments");
                        }
                      }}
                      actions={[<ArrowRightOutlined key="go" />]}
                    >
                      <List.Item.Meta
                        title={
                          <span>
                            <Text type="secondary" style={{ marginRight: 8 }}>
                              {ticket.ticket_number}
                            </Text>
                            {ticket.title}
                          </span>
                        }
                        description={
                          <span>
                            {(ticket.project && ticket.project.name) || ""}
                            {ticket.due_date && (
                              <Text type={overdue ? "danger" : "secondary"} style={{ marginLeft: 12 }}>
                                Due {ticket.due_date}
                              </Text>
                            )}
                          </span>
                        }
                      />
                      <span
                        className="priority-pill"
                        style={{ background: pm.bg, color: pm.color }}
                      >
                        {pm.label}
                      </span>
                    </List.Item>
                  );
                }}
              />
            )}
          </Card>
        </Col>

        <Col xs={24} lg={10}>
          <Card
            title="Projects"
            loading={loading}
            extra={
              <Button type="link" onClick={() => history.push("/tickets/projects")}>
                View all
              </Button>
            }
          >
            {!loading && projects.length === 0 ? (
              <Empty description="Create your first project">
                <Button type="primary" onClick={() => history.push("/tickets/projects/create")}>
                  Create Project
                </Button>
              </Empty>
            ) : (
              <List
                dataSource={projects.slice(0, 6)}
                renderItem={(project) => (
                  <List.Item
                    style={{ cursor: "pointer" }}
                    onClick={() => history.push("/tickets/projects/board/" + project._id)}
                  >
                    <List.Item.Meta
                      avatar={
                        <Avatar style={{ background: project.color || "#6366f1" }}>
                          {project.name.charAt(0).toUpperCase()}
                        </Avatar>
                      }
                      title={project.name}
                      description={project.description || "No description"}
                    />
                    <Tag color={project.status === "ACTIVE" ? "green" : "default"}>{project.status}</Tag>
                  </List.Item>
                )}
              />
            )}
          </Card>

          <Card title="Recent Activity" loading={loading} style={{ marginTop: 16 }}>
            {recentActivity.length === 0 ? (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No recent comments" />
            ) : (
              <List
                size="small"
                dataSource={recentActivity}
                renderItem={(c) => (
                  <List.Item>
                    <List.Item.Meta
                      avatar={<Avatar size="small">{userInitials(c.created_by)}</Avatar>}
                      title={
                        <Text>
                          <strong>{userDisplayName(c.created_by)}</strong> commented on{" "}
                          {(c.ticket && c.ticket.ticket_number) || ""}
                        </Text>
                      }
                      description={
                        c.body
                          ? c.body.slice(0, 80) + (c.body.length > 80 ? "…" : "")
                          : ""
                      }
                    />
                  </List.Item>
                )}
              />
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
}
